import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import axios from 'axios';

import './dashboard.css';
import './estadisticas.css';

import DashboardSidebar from '../components/Dashboard/DashboardSidebar';
import EstadisticasKPICards from '../components/Estadisticas/EstadisticasKPICards';
import EstadisticasTipoChart from '../components/Estadisticas/EstadisticasTipoChart';
import EstadisticasProductoresTop from '../components/Estadisticas/EstadisticasProductoresTop';
import EstadisticasEstadoChart from '../components/Estadisticas/EstadisticasEstadoChart';
import EstadisticasTendencia from '../components/Estadisticas/EstadisticasTendencia';
import EstadisticasResponsabilidad, { getResponsabilidadLabel } from '../components/Estadisticas/EstadisticasResponsabilidad';

function getRecentMonths(count) {
  const list = [];
  const hoy = new Date();
  let currentYear = hoy.getFullYear();
  let currentMonth = hoy.getMonth() + 1;

  for (let i = 0; i < count; i++) {
    list.push({ year: currentYear, month: currentMonth });
    currentMonth--;
    if (currentMonth === 0) {
      currentMonth = 12;
      currentYear--;
    }
  }
  return list;
}

export default function Estadisticas() {
  const [cookies] = useCookies(['user']);
  const [stros, setStros] = useState([]);
  const [estados, setEstados] = useState([]);
  const [pas, setPas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthRange, setMonthRange] = useState(6); // default to 6 months

  // Interactive filtering states
  const [activeProductor, setActiveProductor] = useState(null);
  const [activeEstado, setActiveEstado] = useState(null);
  const [activeResponsabilidad, setActiveResponsabilidad] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isTableExpanded, setIsTableExpanded] = useState(true);

  const loadedMonthsRef = useRef(new Set());

  const fetchMonths = useCallback(async (monthsList) => {
    const toFetch = monthsList.filter(m => {
      const key = `${m.year}-${m.month.toString().padStart(2, '0')}`;
      return !loadedMonthsRef.current.has(key);
    });

    if (toFetch.length === 0) return;

    toFetch.forEach(m => {
      const key = `${m.year}-${m.month.toString().padStart(2, '0')}`;
      loadedMonthsRef.current.add(key);
    });

    for (const m of toFetch) {
      try {
        const mesStr = m.month.toString().padStart(2, '0');
        const res = await axios.get(`https://back.agsseguros.online/api/buscar/${m.year}/${mesStr}`);
        setStros(prev => {
          const existingIds = new Set(prev.map(d => d.id));
          const nuevos = res.data.filter(d => !existingIds.has(d.id));
          return [...prev, ...nuevos];
        });
      } catch (err) {
        console.error(`Error al buscar ${m.year}-${m.month}:`, err);
        const key = `${m.year}-${m.month.toString().padStart(2, '0')}`;
        loadedMonthsRef.current.delete(key);
      }
    }
  }, []);

  // Fetch static data once on mount
  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const estadosRes = await axios.get('https://back.agsseguros.online/api/estados/estadosAll');
        setEstados(estadosRes.data);

        try {
          const pasRes = await axios.get('https://back.agsseguros.online/api/estadisticas/todosLosPas');
          setPas(pasRes.data);
        } catch (e) {
          console.warn('Failing production PAS, falling back to local...', e);
          try {
            const pasRes = await axios.get('http://localhost:8080/api/estadisticas/todosLosPas');
            setPas(pasRes.data);
          } catch (localErr) {
            console.error('Failed to load PAS completely:', localErr);
          }
        }
      } catch (error) {
        console.error('Error loading static data:', error);
      }
    };

    if (cookies.user) {
      fetchStaticData();
    }
  }, [cookies.user]);

  // Load claims data when monthRange changes
  useEffect(() => {
    const fetchClaimsData = async () => {
      try {
        setLoading(true);
        const months = getRecentMonths(monthRange);
        await fetchMonths(months);
        setLoading(false);
      } catch (error) {
        console.error('Error loading claims:', error);
        setLoading(false);
      }
    };

    if (cookies.user) {
      fetchClaimsData();
    }
  }, [cookies.user, monthRange, fetchMonths]);

  // Get date range filter key set
  const selectedRangeMonths = useMemo(() => {
    return getRecentMonths(monthRange);
  }, [monthRange]);

  // 1. Filter claims within range first
  const strosInRange = useMemo(() => {
    const monthKeys = new Set(
      selectedRangeMonths.map(m => `${m.year}-${m.month.toString().padStart(2, '0')}`)
    );
    return stros.filter(s => {
      const dateStr = s.fechaStro || s.fechaRecepcion || '';
      if (!dateStr) return false;
      const key = dateStr.substring(0, 7); // "YYYY-MM"
      return monthKeys.has(key);
    });
  }, [stros, selectedRangeMonths]);

  // 2. Apply interactive filters
  const filteredStros = useMemo(() => {
    return strosInRange.filter(s => {
      // Filter by Producer
      if (activeProductor !== null && Number(s.codp) !== activeProductor) {
        return false;
      }
      // Filter by Estado
      if (activeEstado !== null) {
        if (s.estados && Array.isArray(s.estados)) {
          if (!s.estados.includes(activeEstado)) return false;
        } else if (Number(s.estadoNuevo) !== activeEstado) {
          return false;
        }
      }
      // Filter by Responsabilidad
      if (activeResponsabilidad !== null) {
        const sLabel = getResponsabilidadLabel(s.catStro);
        if (sLabel !== activeResponsabilidad) return false;
      }
      // Filter by search text
      if (searchText.trim()) {
        const query = searchText.toLowerCase();
        const nroStro = (s.nroStro || '').toString().toLowerCase();
        const dom = (s.dom || '').toString().toLowerCase();
        const entryId = (s.entryId || '').toString().toLowerCase();
        const pza = (s.pza || '').toString().toLowerCase();
        const nombre = (s.nombre || s.nombreAseg || '').toString().toLowerCase();

        const match = nroStro.includes(query) ||
                      dom.includes(query) ||
                      entryId.includes(query) ||
                      pza.includes(query) ||
                      nombre.includes(query);
        if (!match) return false;
      }
      return true;
    });
  }, [strosInRange, activeProductor, activeEstado, activeResponsabilidad, searchText]);

  // Calculate KPIs based on the active filtered dataset
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  const totalFilteredCount = filteredStros.length;

  const esteMesCount = useMemo(() => {
    const currentMonthStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}`;
    return filteredStros.filter(s => {
      const dStr = s.fechaStro || s.fechaRecepcion || '';
      return dStr.startsWith(currentMonthStr);
    }).length;
  }, [filteredStros, today]);

  const promedioMensual = useMemo(() => {
    if (monthRange === 0) return 0;
    return (totalFilteredCount / monthRange).toFixed(1);
  }, [totalFilteredCount, monthRange]);

  const vencidosCount = useMemo(() => {
    return filteredStros.filter(s => {
      if (!s.fechaVencimiento) return false;
      return s.fechaVencimiento < todayStr;
    }).length;
  }, [filteredStros, todayStr]);

  // Clear all filters helper
  const handleClearFilters = () => {
    setActiveProductor(null);
    setActiveEstado(null);
    setActiveResponsabilidad(null);
    setSearchText('');
  };

  const hasActiveFilters = activeProductor !== null || activeEstado !== null || activeResponsabilidad !== null || searchText !== '';

  const formatToday = () => {
    return today.toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // State map for resolving cell display name
  const estadoMap = useMemo(() => {
    const map = {};
    estados.forEach(e => {
      map[e.id] = e.nombre || e.estado || `Estado ${e.id}`;
    });
    return map;
  }, [estados]);

  // PAS lookup map for resolving producer names in detail table
  const pasNameMap = useMemo(() => {
    const map = {};
    pas.forEach(p => {
      map[Number(p.pro_productor)] = p.pro_raz_social;
    });
    return map;
  }, [pas]);

  if (loading && stros.length === 0) {
    return (
      <div className="est-loading">
        <div className="est-loading-spinner"></div>
        <p style={{ fontFamily: 'Manrope, sans-serif', color: '#64748b', fontWeight: 600 }}>Cargando panel de estadísticas...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <DashboardSidebar user={cookies.user} />

      <main className="est-container">
        {/* Dashboard Header */}
        <div className="est-header">
          <div className="est-title-section">
            <h1>Estadísticas de Siniestros</h1>
            <p>Visualización y análisis de métricas clave de siniestros cargados.</p>
          </div>
          <div className="est-controls">
            <button
              className={`est-range-btn ${monthRange === 3 ? 'active' : ''}`}
              onClick={() => setMonthRange(3)}
            >
              Últimos 3 Meses
            </button>
            <button
              className={`est-range-btn ${monthRange === 6 ? 'active' : ''}`}
              onClick={() => setMonthRange(6)}
            >
              Últimos 6 Meses
            </button>
            <button
              className={`est-range-btn ${monthRange === 12 ? 'active' : ''}`}
              onClick={() => setMonthRange(12)}
            >
              Último Año
            </button>
            <div className="dashboard-date-badge" style={{ marginLeft: '12px' }}>
              <i className="bi bi-calendar3"></i>
              {formatToday()}
            </div>
          </div>
        </div>

        {/* Filters Summary Ribbon */}
        {hasActiveFilters && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'rgba(66, 195, 186, 0.05)',
            border: '1px dashed #42C3BA',
            padding: '12px 20px',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', fontSize: '13px', color: '#0d9488', fontWeight: 600 }}>
              <i className="bi bi-funnel-fill"></i>
              Filtros activos:
              {activeProductor && <span className="est-card-badge" style={{ border: '1px solid #42C3BA' }}>Productor: {pasNameMap[activeProductor] || activeProductor}</span>}
              {activeEstado && <span className="est-card-badge" style={{ border: '1px solid #42C3BA' }}>Estado: {estadoMap[activeEstado] || activeEstado}</span>}
              {activeResponsabilidad && <span className="est-card-badge" style={{ border: '1px solid #42C3BA' }}>Responsabilidad: {activeResponsabilidad}</span>}
              {searchText && <span className="est-card-badge" style={{ border: '1px solid #42C3BA' }}>Búsqueda: "{searchText}"</span>}
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleClearFilters}
              style={{ fontSize: '12px', borderRadius: '10px' }}
            >
              Limpiar Todos
            </button>
          </div>
        )}

        {/* KPI Cards Component */}
        <EstadisticasKPICards
          totalStros={totalFilteredCount}
          esteMes={esteMesCount}
          promedioMensual={promedioMensual}
          vencidos={vencidosCount}
        />

        {/* Charts Row 1: Tendency and Claim Types */}
        <div className="est-dashboard-grid">
          <EstadisticasTendencia stros={strosInRange} />
          <EstadisticasTipoChart stros={filteredStros} />
        </div>

        {/* Charts Row 2: Producer Rankings, Estado chips and Responsabilidad chips */}
        <div className="est-dashboard-grid-three">
          <EstadisticasProductoresTop
            stros={strosInRange}
            pas={pas}
            activeProductor={activeProductor}
            onSelectProductor={setActiveProductor}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <EstadisticasEstadoChart
              stros={strosInRange}
              estados={estados}
              activeEstado={activeEstado}
              onSelectEstado={setActiveEstado}
            />
            <EstadisticasResponsabilidad
              stros={strosInRange}
              activeResponsabilidad={activeResponsabilidad}
              onSelectResponsabilidad={setActiveResponsabilidad}
            />
          </div>
        </div>

        {/* Searchable Details Table */}
        <div className="est-detail-section">
          <div className="est-detail-header" onClick={() => setIsTableExpanded(!isTableExpanded)}>
            <span className="est-detail-title">
              <i className="bi bi-list-nested"></i>
              Detalle de Casos Filtrados
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }} onClick={e => e.stopPropagation()}>
              <input
                type="text"
                placeholder="Buscar por nro, dominio, póliza o asegurado..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  fontSize: '13px',
                  width: '320px',
                  outline: 'none',
                  fontFamily: 'Manrope, sans-serif'
                }}
              />
              <span className="est-card-badge" style={{ fontSize: '11px' }}>
                {totalFilteredCount} registros
              </span>
              <button
                className="btn btn-link p-0 text-secondary"
                onClick={() => setIsTableExpanded(!isTableExpanded)}
                style={{ textDecoration: 'none' }}
              >
                <i className={`bi ${isTableExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: '16px' }}></i>
              </button>
            </div>
          </div>

          {isTableExpanded && (
            <div className="est-detail-body">
              {filteredStros.length === 0 ? (
                <div className="est-empty" style={{ padding: '60px' }}>
                  <i className="bi bi-search"></i>
                  <p>No se encontraron siniestros que coincidan con los filtros aplicados.</p>
                </div>
              ) : (
                <table className="est-detail-table">
                  <thead>
                    <tr>
                      <th>Siniestro</th>
                      <th>Denuncia</th>
                      <th>Póliza</th>
                      <th>Patente</th>
                      <th>Asegurado</th>
                      <th>Fecha</th>
                      <th>Responsabilidad</th>
                      <th>Estado</th>
                      <th>Asignado</th>
                      <th style={{ textAlign: 'center' }}>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStros.slice(0, 100).map(s => {
                      const formattedDate = s.fechaStro
                        ? s.fechaStro.split('-').reverse().join('/')
                        : s.fechaRecepcion
                        ? s.fechaRecepcion.split('-').reverse().join('/')
                        : '—';

                      const resolvedEstado = s.estados && Array.isArray(s.estados) && s.estados.length > 0
                        ? s.estados.map(id => estadoMap[id] || id).join(', ')
                        : estadoMap[s.estadoNuevo] || s.estadoNuevo || 'S/E';

                      return (
                        <tr key={s.id}>
                          <td style={{ fontWeight: 700, color: '#0f172a' }}>
                            {s.nroStro?.toString().startsWith('000') ? s.nroStro.substring(3) : s.nroStro || '—'}
                          </td>
                          <td>{s.entryId || '—'}</td>
                          <td>{s.pza || '—'}</td>
                          <td style={{ fontWeight: 600, color: '#475569' }}>{s.dom || '—'}</td>
                          <td>{s.nombre || s.nombreAseg || '—'}</td>
                          <td>{formattedDate}</td>
                          <td>
                            <span style={{ fontSize: '12px', fontWeight: 500, color: '#475569' }}>
                              {getResponsabilidadLabel(s.catStro)}
                            </span>
                          </td>
                          <td>
                            <span className="est-status-badge" style={{ backgroundColor: 'rgba(66, 195, 186, 0.08)', color: '#0d9488', border: '1px solid rgba(66, 195, 186, 0.2)' }}>
                              {resolvedEstado}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600, color: '#475569' }}>{s.empleadoAsignado || '—'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <Link to={`/${s.id}`} className="est-action-btn">
                              <i className="bi bi-arrow-right"></i>
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {filteredStros.length > 100 && (
                <div style={{ textAlign: 'center', padding: '16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                  Mostrando los primeros 100 registros. Refine sus filtros o búsqueda para ver más casos específicos.
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
